import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import API from '../../api/api';

const RunInformationForm = ({
  jobId,
  wellId: propWellId,
  formData = {},            // <- default prop to {} to avoid undefined
  setFormData,
  onSuccess,
  isNewProject
}) => {
  // safeFormData ensures reads never access undefined
  const safeFormData = formData || {};

  const [surveyTypes, setSurveyTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [wellId, setWellId] = useState(propWellId || null);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [holeSections, setHoleSections] = useState([]);
  const [surveyRunIns, setSurveyRunIns] = useState([]);
  const [minimumIds, setMinimumIds] = useState([]);
  const [wellTypes, setWellTypes] = useState([]);

  const [filteredSurveyRunIns, setFilteredSurveyRunIns] = useState([]);
  const [filteredMinimumIds, setFilteredMinimumIds] = useState([]);

  const [isCasing, setIsCasing] = useState(false);
  const [isDrillPipe, setIsDrillPipe] = useState(false);

  useEffect(() => {
    if (propWellId) setWellId(propWellId);
  }, [propWellId]);

  useEffect(() => {
    const fetchSurveyTypes = async () => {
      try {
        const res = await API.get('/surveytype/');
        setSurveyTypes(res.data || []);
      } catch {
        toast.error('Failed to load survey types');
      }
    };
    fetchSurveyTypes();
  }, []);

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [holeRes, runInRes, minIdRes, wellTypesRes] = await Promise.all([
          API.get('/holesection/'),
          API.get('/surveyrunin/'),
          API.get('/minimumid/'),
          API.get('/welltype/')
        ]);
        setHoleSections(holeRes.data || []);
        setSurveyRunIns(runInRes.data || []);
        setMinimumIds(minIdRes.data || []);
        setWellTypes(wellTypesRes.data || []);
      } catch {
        toast.error('Failed to load master data');
      }
    };
    fetchMasters();
  }, []);

  const parseSize = (sizeString) => {
    if (!sizeString) return 0;
    const cleanString = sizeString.replace(/"/g, '').replace(/[^\d\s\.\/]/g, '');
    if (cleanString.includes('/')) {
      const parts = cleanString.split(' ');
      if (parts.length === 2) {
        const whole = parseFloat(parts[0]) || 0;
        const [num, den] = parts[1].split('/').map(Number);
        if (!isNaN(num) && !isNaN(den)) return whole + num / den;
      } else if (parts.length === 1) {
        const [num, den] = parts[0].split('/').map(Number);
        if (!isNaN(num) && !isNaN(den)) return num / den;
      }
    }
    return parseFloat(cleanString) || 0;
  };

  // --- Effects use safeFormData for reads ---

  useEffect(() => {
    const holeId = safeFormData.hole_section;
    if (!holeId) {
      setFilteredSurveyRunIns(surveyRunIns);
      setIsDrillPipe(false);
      setIsCasing(false);
      return;
    }

    const selectedHoleSection = holeSections.find(section => section.hole_section_id == holeId);
    if (!selectedHoleSection) {
      setFilteredSurveyRunIns(surveyRunIns);
      return;
    }

    const holeSectionSize = parseSize(selectedHoleSection.hole_section_name);
    let filteredRunIns = surveyRunIns.filter(runIn => parseSize(runIn.survey_runin_name) <= holeSectionSize);

    const holeSectionName = selectedHoleSection.hole_section_name.toLowerCase();
    if (holeSectionName.includes('4 1/2')) {
      setIsDrillPipe(true);
      setIsCasing(false);
      filteredRunIns = filteredRunIns.filter(runIn => runIn.survey_runin_name.toLowerCase().includes('drillpipe'));
    } else {
      setIsDrillPipe(false);
      setIsCasing(false);
    }

    setFilteredSurveyRunIns(filteredRunIns);
  }, [safeFormData.hole_section, holeSections, surveyRunIns]);

  useEffect(() => {
    const runInId = safeFormData.survey_run_in;
    if (!runInId) {
      setFilteredMinimumIds(minimumIds);
      return;
    }

    const selectedSurveyRunIn = surveyRunIns.find(runIn => runIn.survey_runin_id == runInId);
    if (!selectedSurveyRunIn) {
      setFilteredMinimumIds(minimumIds);
      return;
    }

    const runInSize = parseSize(selectedSurveyRunIn.survey_runin_name);
    const filteredMinIds = minimumIds.filter(minId => parseSize(minId.minimum_id_name) < runInSize);
    setFilteredMinimumIds(filteredMinIds);

    if (filteredMinIds.length === 1) {
      setFormData(prev => ({ ...(prev || {}), minimum_id: filteredMinIds[0].minimum_id }));
    }
  }, [safeFormData.survey_run_in, surveyRunIns, minimumIds, setFormData]);

  useEffect(() => {
    const surveyFrom = parseFloat(safeFormData.survey_from);
    const surveyTo = parseFloat(safeFormData.survey_to);
    if (!isNaN(surveyFrom) && !isNaN(surveyTo) && surveyTo >= surveyFrom) {
      setFormData(prev => ({ ...(prev || {}), survey_meterage: (surveyTo - surveyFrom).toFixed(2) }));
    } else {
      setFormData(prev => ({ ...(prev || {}), survey_meterage: '' }));
    }
  }, [safeFormData.survey_from, safeFormData.survey_to, setFormData]);

  useEffect(() => {
    const expIncl = parseFloat(safeFormData.exp_inclination);
    if (isNaN(expIncl) || wellTypes.length === 0) return;

    let wellTypeId = null;
    if (expIncl <= 5) {
      wellTypeId = wellTypes.find(t => t?.well_type?.toLowerCase().includes('vertical'))?.well_type_id || null;
    } else {
      wellTypeId = wellTypes.find(t => t?.well_type?.toLowerCase().includes('deviated'))?.well_type_id || null;
    }
    if (wellTypeId) setFormData(prev => ({ ...(prev || {}), well_type: wellTypeId }));
  }, [safeFormData.exp_inclination, wellTypes, setFormData]);

  useEffect(() => {
    const fetchRunInfo = async () => {
      if (isNewProject || !wellId || !jobId || hasFetchedData) return;
      try {
        const res = await API.get(`/runs/?well=${wellId}&job=${jobId}`);
        const matchingRun = res.data?.find(r => r.well == wellId && r.job == jobId);
        if (matchingRun) setFormData(matchingRun);
        setHasFetchedData(true);
      } catch (err) {
        console.error('Error fetching run info:', err);
      }
    };
    fetchRunInfo();
  }, [jobId, wellId, hasFetchedData, isNewProject, setFormData]);

  // safer handleChange: preserves prev even if prev is undefined
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...(prev || {}), [name]: value }));
  };

  const handleSurveyRunInTypeChange = (type) => {
    if (type === 'casing') {
      setIsCasing(true); setIsDrillPipe(false);
      setFilteredSurveyRunIns(prev => (prev || []).filter(r => r.survey_runin_name.toLowerCase().includes('casing')));
    } else {
      setIsCasing(false); setIsDrillPipe(true);
      setFilteredSurveyRunIns(prev => (prev || []).filter(r => r.survey_runin_name.toLowerCase().includes('drillpipe')));
    }
  };

  const sanitize = (data) => {
    const sanitized = {};
    for (const key in data) {
      const val = data[key];
      sanitized[key] = val === '' ? null : (isNaN(val) || key === 'survey_type' ? val : Number(val));
    }
    return sanitized;
  };

  const handleSubmit = async () => {
    if (!wellId || !jobId) return toast.error('Missing well ID or job ID');

    const surveyFrom = parseFloat(safeFormData.survey_from);
    const surveyTo = parseFloat(safeFormData.survey_to);
    if (isNaN(surveyFrom) || isNaN(surveyTo) || surveyFrom >= surveyTo) return toast.error('Survey From must be less than Survey To');

    setIsSubmitting(true);
    try {
      const payload = sanitize({ ...(safeFormData || {}), well: wellId, job: jobId });
      let response;
      if (safeFormData.id) {
        response = await API.put(`/runs/${safeFormData.id}/`, payload);
        toast.success('Run information updated successfully!');
      } else {
        response = await API.post('/runs/', payload);
        toast.success('Run information saved successfully!');
      }
      if (onSuccess) onSuccess(response.data);
    } catch (err) {
      console.error('Error saving run information:', err?.response?.data);
      toast.error(err?.response?.data?.message || 'Failed to save run information');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fields = [
    { label: 'Survey From', name: 'survey_from', type: 'number', step: '0.01' },
    { label: 'Survey To', name: 'survey_to', type: 'number', step: '0.01' },
    { label: 'Survey Meterage', name: 'survey_meterage', type: 'number', step: '0.01', readOnly: true },
    { label: 'Survey Type', name: 'survey_type', type: 'select', options: surveyTypes.map(t => ({ value: t.survey_type_id, label: t.survey_type })) },
    { label: 'Expected Inclination', name: 'exp_inclination', type: 'number', step: '0.01' },
    { label: 'Well Type', name: 'well_type', type: 'select', options: wellTypes.map(t => ({ value: t.well_type_id, label: t.well_type })), readOnly: true },
    { label: 'Proposed Direction', name: 'proposed_direction', type: 'number', step: '0.01' },
    { label: 'Reference Height', name: 'reference_height', type: 'number', step: '0.01' },
    { label: 'Drill Floor Elevation', name: 'drill_floor_elevation', type: 'number', step: '0.01' },
    { label: 'Expected Temperature', name: 'exp_temp', type: 'number', step: '0.01' }
  ];

  return (
    <div className="p-6 border rounded-lg shadow-md max-w-5xl mx-auto bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-xs">
      <h2 className="text-lg font-semibold mb-4">Run Information</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {/* Hole Section */}
        <div className="flex flex-col">
          <label htmlFor="hole_section" className="mb-1 font-medium">Hole Section</label>
          <select id="hole_section" name="hole_section" value={safeFormData.hole_section ?? ''} onChange={handleChange}
                  className="border rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-700">
            <option value="">Select hole section</option>
            {holeSections.map(s => <option key={s.hole_section_id} value={s.hole_section_id}>{s.hole_section_name}</option>)}
          </select>
        </div>

        {/* Survey Run In Type checkboxes (only when hole is not 4 1/2) */}
        {safeFormData.hole_section && !(String(safeFormData.hole_section).includes('4 1/2')) && (
          <div className="flex flex-col col-span-full">
            <label className="mb-1 font-medium">Survey Run In Type</label>
            <div className="flex space-x-4">
              <label className="flex items-center"><input type="checkbox" checked={isCasing} onChange={() => handleSurveyRunInTypeChange('casing')} className="mr-2" />Casing</label>
              <label className="flex items-center"><input type="checkbox" checked={isDrillPipe} onChange={() => handleSurveyRunInTypeChange('drillPipe')} className="mr-2" />Drill Pipe</label>
            </div>
          </div>
        )}

        {/* Survey Run In */}
        <div className="flex flex-col">
          <label htmlFor="survey_run_in" className="mb-1 font-medium">Survey Run In</label>
          <select id="survey_run_in" name="survey_run_in" value={safeFormData.survey_run_in ?? ''} onChange={handleChange}
                  disabled={!safeFormData.hole_section || (String(safeFormData.hole_section) !== '4 1/2' && !isCasing && !isDrillPipe)}
                  className="border rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-700 disabled:bg-gray-100">
            <option value="">Select survey run in</option>
            {filteredSurveyRunIns.map(r => <option key={r.survey_runin_id} value={r.survey_runin_id}>{r.survey_runin_name}</option>)}
          </select>
        </div>

        {/* Minimum ID */}
        <div className="flex flex-col">
          <label htmlFor="minimum_id" className="mb-1 font-medium">Minimum ID</label>
          <select id="minimum_id" name="minimum_id" value={safeFormData.minimum_id ?? ''} onChange={handleChange}
                  disabled={!safeFormData.survey_run_in}
                  className="border rounded px-2 py-1.5 text-xs bg-white dark:bg-gray-700 disabled:bg-gray-100">
            <option value="">Select minimum ID</option>
            {filteredMinimumIds.map(m => <option key={m.minimum_id} value={m.minimum_id}>{m.minimum_id_name}</option>)}
          </select>
        </div>

        {/* other fields */}
        {fields.map(({ label, name, type, step, options, readOnly }) => (
          <div key={name} className="flex flex-col">
            <label htmlFor={name} className="mb-1 font-medium">{label}</label>
            {type === 'select' ? (
              <select id={name} name={name} value={safeFormData[name] ?? ''} onChange={handleChange} disabled={readOnly}
                      className={`border rounded px-2 py-1.5 text-xs ${readOnly ? 'bg-gray-100' : 'bg-white'}`}>
                <option value="">{`Select ${label.toLowerCase()}`}</option>
                {options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            ) : (
              <input id={name} name={name} type={type} step={step} value={safeFormData[name] ?? ''} onChange={handleChange} readOnly={readOnly}
                     className={`border rounded px-2 py-1.5 text-xs ${readOnly ? 'bg-gray-100' : 'bg-white'}`} />
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 flex justify-end">
        <button onClick={handleSubmit} disabled={isSubmitting || !wellId}
                className={`px-4 py-2 rounded-md text-xs font-medium text-white ${isSubmitting || !wellId ? 'bg-gray-300 text-gray-500' : 'bg-red-600 hover:bg-red-700'}`}>
          {isSubmitting ? 'Saving...' : safeFormData.id ? 'UPDATE RUN INFO' : 'SAVE RUN INFO'}
        </button>
      </div>
    </div>
  );
};

export default RunInformationForm;
